import React, { useState, useEffect } from 'react'
import './index.css'
import { PlanPosition, ApprovalsCategory } from '../../types'
import { Structure } from '../../../../Org/Structure/types'
import { Plan } from '../../../types'

import { CurrencyList } from '../../../Positions/Edit/types'
import HoverTooltip from '../../../../../../components/HoverTooltip'

type Props = {
    keyindex: number
    planPosition: PlanPosition
    structData: Structure[]
    onDoubleClick: (plan_reg_number: string, pos_guid: string) => void
    theme: string
    currency: CurrencyList[]
}

type ApiResponsePlan = {
    message: Plan[]
}

const POSITION_STATUS = new Map([
    ['P', 'Размещена'],
    ['A', 'Аннулирована'],
    ['N', 'Новая'],
    ['C', 'Изменение'],
])

const POSITION_DRAFT_STATUS = new Map([
    ['P', 'Размещение'],
    ['A', 'Аннулирование позиции'],
    ['N', 'Новая позиция'],
    ['C', 'Изменение позиции'],
])

const MONTHS = [
    'январь',
    'февраль',
    'март',
    'апрель',
    'май',
    'июнь',
    'июль',
    'август',
    'сентябрь',
    'октябрь',
    'ноябрь',
    'декабрь',
]

const CURRENCY = new Map([
    ['643', '₽'],
    ['156', '¥'],
    ['978', '€'],
    ['840', '$'],
])

const GWS = new Map([
    ['G', 'Товар'],
    ['W', 'Работа'],
    ['S', 'Услуга'],
])

const ApprovalsTableRow: React.FC<Props> = ({
    keyindex,
    planPosition,
    structData,
    onDoubleClick,
    theme,
    currency,
}) => {
    const [isLoadingPlan, setLoadingPlan] = useState<boolean>(true)
    const [plan, setPlan] = useState<Plan[]>([])

    const getNameStruct = (struct_guid: string) => {
        const nameSctruct = structData.find((s) => s.guid === struct_guid)
        return nameSctruct?.struct_short_name || '-'
    }

    const getBackgroundColor = (endDateStr: string | null): string => {
        if (!endDateStr) return ''

        const today = new Date()
        const endDate = new Date(endDateStr)
        const diffTime = endDate.getTime() - today.getTime()
        const diffDays = diffTime / (1000 * 60 * 60 * 24)

        if (diffDays <= 0) {
            // Если дата наступила или прошла – красный фон
            return 'bg-[#ed9cae] dark:bg-[#C15C5C]'
        } else if (diffDays < 2) {
            // Если осталось меньше 2 дней – оранжевый фон
            return 'bg-[#FAB502] dark:bg-[#C1AB5C]'
        }
        return ''
    }

    const getEndDateColor = (endDateStr: string | null): string => {
        if (!endDateStr) return ''

        const today = new Date()
        const endDate = new Date(endDateStr)
        const diffTime = endDate.getTime() - today.getTime()
        const diffDays = diffTime / (1000 * 60 * 60 * 24)

        if (diffDays <= 0) {
            // Если дата наступила или прошла – красный фон
            return 'text-[#611212]'
        } else if (diffDays < 2) {
            // Если осталось меньше 2 дней – оранжевый фон
            return 'text-[#3A2506]'
        }
        return 'text-[#181818] dark:text-[#FFFFFF]'
    }

    const loadPlan = (guid: string) => {
        setLoadingPlan(true)

        fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v2/plans?plan_guid=${guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to fetch plan')
                }
            })
            .then((response: ApiResponsePlan) => response.message)
            .catch((e) => {
                console.error(e)
                return []
            })
            .then((response) => {
                setPlan(response)
            })
            .finally(() => {
                setLoadingPlan(false)
            })
    }

    const STATUS_COLOR =
        theme == 'dark'
            ? new Map([
                  ['Черновик', '#4E9AFF'],
                  ['Готово', '#049664'],
                  ['Подготовка', '#3C69FE'],
                  ['Готово к отправке', '#2235FF'],
                  ['Отправлено в ЕИС', '#049664'],
                  ['Размещена', '#1114CB'],
                  ['Аннулирована', '#333033'],
              ])
            : new Map([
                  ['Черновик', '#D6EFFF'],
                  ['Готово', '#83D2B7'],
                  ['Подготовка', '#C7E4FE'],
                  ['Готово к отправке', '#ABD7FE'],
                  ['Отправлено в ЕИС', '#83D2B7'],
                  ['Размещена', '#90C0FF'],
                  ['Аннулирована', '#F6FBFF'],
              ])

    const STAGE_COLOR =
        theme == 'dark'
            ? new Map([
                  ['Редактирование', '#3C69FE'],
                  ['Подготовка к размещению', '#2235FF'],
                  ['Отправка в ЕИС', '#1114CB'],
                  ['План закупки в ЕИС', '#231D73'],
              ])
            : new Map([
                  ['Редактирование', '#C7E4FE'],
                  ['Подготовка к размещению', '#ABD7FE'],
                  ['Отправка в ЕИС', '#90C0FF'],
                  ['План закупки в ЕИС', '#A0BBF4'],
              ])

    const POSITION_STATUS_COLOR =
        theme == 'dark'
            ? new Map([
                  ['N', '#3C69FE'],
                  ['C', '#563AF5'],
                  ['A', '#3A3A3C'],
                  ['P', '#3C18BC'],
              ])
            : new Map([
                  ['N', '#C7E4FE'],
                  ['C', '#ABD7FE'],
                  ['A', '#ED9CAE'],
                  ['P', '#90C0FF'],
              ])

    const POSITION_DRAFT_STATUS_COLOR =
        theme == 'dark'
            ? new Map([
                  ['N', '#8E8E93'],
                  ['C', '#636366'],
                  ['A', '#3A3A3C'],
                  ['P', '#90C0FF'],
              ])
            : new Map([
                  ['N', '#E4E8F0'],
                  ['C', '#CFD6E2'],
                  ['A', '#B7BECA'],
                  ['P', '#90C0FF'],
              ])

    return (
        <>
            <div
                className={`${planPosition.is_saw == true ? 'flex flex-row gap-1 mx-1 cursor-pointer rounded text-[11px] text-[#181818] dark:text-[#FFFFFF] bg-[#ebebeb] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]' : 'flex flex-row gap-1 mx-1 cursor-pointer bg-[#f3f3f3] hover:bg-[#FFFFFF] dark:bg-[#373538] dark:hover:bg-[#464447] text-[#181818] dark:text-[#FFFFFF] text-[11px] rounded'}`}
                onClick={() => loadPlan(planPosition.plan_guid)}
                onDoubleClick={() => {
                    // loadPlan(planPosition.plan_guid)
                    onDoubleClick(
                        String(plan[0].registration_number),
                        planPosition.guid
                    )
                }}
            >
                <div className="flex h-[70px] w-12 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                    {keyindex}
                </div>
                <HoverTooltip
                    tooltipText={
                        planPosition.approval_status == 0
                            ? 'Идет согласование'
                            : planPosition.approval_status == 1 ||
                                planPosition.approval_status == 2
                              ? 'Согласовано'
                              : planPosition.approval_status == 3
                                ? 'Не согласовано'
                                : planPosition.approval_status == 4
                                  ? 'Отменено'
                                  : planPosition.approval_status == null
                                    ? 'Отсутствует'
                                    : ''
                    }
                >
                    <div className="flex h-[70px] w-10 items-center justify-center">
                        <div
                            className={`flex flex-row w-4 h-4  rounded-full 
                                ${
                                    theme == 'dark'
                                        ? planPosition.approval_status == 0
                                            ? 'bg-[#FAB502]'
                                            : planPosition.approval_status ==
                                                    1 ||
                                                planPosition.approval_status ==
                                                    2
                                              ? 'bg-[#049664]'
                                              : planPosition.approval_status ==
                                                  3
                                                ? 'bg-[#C15C5C]'
                                                : planPosition.approval_status ==
                                                    4
                                                  ? 'bg-[#464447]'
                                                  : planPosition.approval_status ==
                                                      null
                                                    ? 'bg-[#464447]'
                                                    : ''
                                        : planPosition.approval_status == 0
                                          ? 'bg-[#FAB502]'
                                          : planPosition.approval_status == 1 ||
                                              planPosition.approval_status == 2
                                            ? 'bg-[#83d2b7]'
                                            : planPosition.approval_status == 3
                                              ? 'bg-[#ed9cae]'
                                              : planPosition.approval_status ==
                                                  4
                                                ? 'bg-[#d0d0d0]'
                                                : planPosition.approval_status ==
                                                    null
                                                  ? 'bg-[#d0d0d0]'
                                                  : ''
                                }
                                `}
                        ></div>
                    </div>
                </HoverTooltip>
                <div className="flex h-[70px] w-20 items-center justify-center text-center text-[#181818] dark:text-[#FFFFFF]">
                    {planPosition.start_date
                        ?.split('-')
                        .reverse()
                        .join('-')
                        .replace(/[\-]/g, '.')}
                </div>
                <div className="flex h-[70px] w-20 items-center justify-center text-center text-[#181818] dark:text-[#FFFFFF]">
                    <div
                        className={`flex w-fit h-fit px-1 rounded-[5px] items-center justify-center text-center ${getEndDateColor(planPosition.end_date)} ${getBackgroundColor(planPosition.end_date)}`}
                    >
                        {planPosition.end_date
                            ?.split('-')
                            .reverse()
                            .join('-')
                            .replace(/[\-]/g, '.')}
                    </div>
                </div>
                <div
                    className={`flex h-[70px] w-24 items-center justify-center text-center rounded`}
                    style={{
                        backgroundColor: planPosition.position_stage
                            ? STAGE_COLOR.get(planPosition.position_stage)
                            : 'transparent',
                    }}
                >
                    {planPosition.position_stage}
                </div>
                <div
                    className="flex h-[70px] w-24 items-center justify-center text-center rounded"
                    style={{
                        backgroundColor: planPosition.position_status
                            ? STATUS_COLOR.get(planPosition.position_status)
                            : 'transparent',
                    }}
                >
                    {planPosition.position_status}
                </div>
                <HoverTooltip tooltipText={planPosition.contract_subject}>
                    <div className="flex h-[70px] flex-1 items-center justify-start text-left min-w-44 px-2 ">
                        <p className="fourrow">
                            {planPosition.contract_subject
                                ? planPosition.contract_subject
                                : '-'}
                        </p>
                    </div>
                </HoverTooltip>
                <div className="flex h-[70px] w-28 items-center justify-end text-right px-2 ">
                    <HoverTooltip
                        tooltipText={
                            planPosition.currency_digital_code
                                ? String(
                                      currency.find(
                                          (c) =>
                                              c.num_code ===
                                              String(
                                                  planPosition.currency_digital_code
                                              )
                                      )?.name
                                  )
                                : '-'
                        }
                    >
                        <span>
                            {Number(
                                planPosition.maximum_contract_price
                            ).toLocaleString('ru-RU', {
                                minimumFractionDigits: 2,
                            })}{' '}
                            {planPosition.currency_digital_code
                                ? planPosition.currency_digital_code == 643
                                    ? ''
                                    : currency.find(
                                          (c) =>
                                              c.num_code ===
                                              String(
                                                  planPosition.currency_digital_code
                                              )
                                      )?.char_sign || '?'
                                : ''}
                        </span>
                    </HoverTooltip>
                </div>
                <div className="flex h-[70px] w-28 items-center justify-end text-right px-2 ">
                    {planPosition.purchase_planned_date === null ? (
                        planPosition.purchase_period_month ? (
                            <>
                                {MONTHS[
                                    Number(planPosition.purchase_period_month) -
                                        1
                                ] + ' '}
                                {planPosition.purchase_period_year}
                            </>
                        ) : (
                            '-'
                        )
                    ) : (
                        planPosition.purchase_planned_date
                            ?.split('-')
                            .reverse()
                            .join('-')
                            .replace(/[\-]/g, '.')
                    )}
                </div>
                <div className="flex h-[70px] w-28 items-center justify-end text-right px-2 ">
                    {planPosition.contract_end_date ? (
                        <>
                            {MONTHS[
                                Number(
                                    planPosition.contract_end_date?.slice(5, 7)
                                ) - 1
                            ] + ' '}
                            {planPosition.contract_end_date?.slice(0, 4)}
                        </>
                    ) : (
                        '-'
                    )}
                </div>
                <div className="flex h-[70px] w-44 items-center justify-center text-center">
                    <p className="fourrow">
                        {planPosition.purchase_method_name
                            ? planPosition.purchase_method_name
                            : '-'}
                    </p>
                </div>
                <div
                    className={
                        'flex h-[70px] w-24 items-center justify-center text-center rounded'
                    }
                    style={{
                        backgroundColor: planPosition.status
                            ? POSITION_DRAFT_STATUS_COLOR.get(
                                  planPosition.status
                              )
                            : 'transparent',
                    }}
                >
                    {POSITION_DRAFT_STATUS.get(String(planPosition.status))}
                </div>
                <div className="flex h-[70px] w-24 items-center justify-center text-center">
                    {planPosition.type_object_purchase_position
                        ? GWS.get(
                              String(planPosition.type_object_purchase_position)
                          )
                        : '-'}
                </div>
                <HoverTooltip
                    tooltipText={`${planPosition.struct_name}`}
                    align="center"
                >
                    <div className="flex h-[70px] w-24 items-center justify-center text-center">
                        {planPosition.struct_guid !== undefined &&
                        planPosition.struct_guid !== null &&
                        planPosition.struct_guid !== ''
                            ? getNameStruct(planPosition.struct_guid)
                            : '-'}
                    </div>
                </HoverTooltip>
                {/* <div className="flex h-[70px] w-20 items-center justify-center text-center">
                    {planPosition.expense_items_name !== undefined &&
                    planPosition.expense_items_name !== null &&
                    planPosition.expense_items_name !== ''
                        ? `${planPosition.expense_items_name}`
                        : '-'}
                </div> */}
                <div className="flex h-[70px] w-20 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                    {planPosition.position_version}
                </div>
                <div className="flex h-[70px] w-20 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                    {planPosition.sys_updated_at
                        ?.slice(0, 10)
                        .split('-')
                        .reverse()
                        .join('-')
                        .replace(/[\-]/g, '.')}
                    <br />
                    {planPosition.sys_updated_at?.slice(11, 16)}
                </div>
                <div className="flex h-[70px] w-14 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                    <HoverTooltip
                        tooltipText={
                            planPosition.first_name !== null
                                ? planPosition.last_name +
                                  ' ' +
                                  planPosition.first_name
                                : 'Отсутствует'
                        }
                        align="left"
                    >
                        <div className="flex h-8 w-8 rounded-full items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d9dce0] dark:bg-[#333033]">
                            {planPosition.first_name !== null
                                ? planPosition.last_name?.charAt(0) +
                                  planPosition.first_name?.charAt(0)
                                : 'О'}
                        </div>
                    </HoverTooltip>
                </div>
            </div>
        </>
    )
}

export default ApprovalsTableRow
